import {Component, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {HttpUtils, Utils} from '@astek/utils';
import {LoggerService} from '../../../core/services/logger/logger.service';
import {FormBuilder, Validators} from '@angular/forms';
import {confirmPasswordValidator} from '../../../core/services/user/validator/confirm-password.validator';
import {AuthenticationRestApiService} from '../services/http/authentication-rest-api.service';
import {
  ApiUserDataFromRegistrationTokenResponse
} from '../services/http/api-user-data-from-registration-token-response';
import {HttpErrorResponse, HttpStatusCode} from '@angular/common/http';
import {TranslocoService} from '@jsverse/transloco';
import {UserRolesEnum} from '../../../core/services/user/user-roles.enum';
import {ApiRegistrationRequest} from '../services/http/api-registration-request';
import {MatSnackBar} from '@angular/material/snack-bar';
import {NavigationService} from '../../../core/services/navigation/navigation.service';
import {RoutesEnum} from '../../../core/services/navigation/routes.enum';
import {ApiRegistrationKnownErrorsEnum} from '../services/http/api-registration-known-errors.enum';

@Component({
  selector: 'xp-register',
  templateUrl: './register.component.html',
  styleUrls: ['../authentication.common.scss', './register.component.scss'],
})
export class RegisterComponent implements OnInit {

  // At least 12 characters, including lowercase, uppercase and numeric
  // and special chars in: . , / ! @ # $ % ^ & * ( ) - _ { } [ ] : ;
  public readonly pwdRegexp: RegExp = /^.*(?=.{12,})(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[.,/!@#$%^&*()\-_{}[\]:;]).*$/;
  public readonly token: string = '';

  public user: ApiUserDataFromRegistrationTokenResponse | null = null;

  public isLoading: boolean = true;
  public error: string = '';

  public readonly formGroup = this._formBuilder.group({
    email: [{value: '', disabled: true}],
    password: ['', [Validators.required.bind(Validators), Validators.pattern(this.pwdRegexp).bind(Validators)]],
    confirm: ['', [Validators.required.bind(Validators), Validators.pattern(this.pwdRegexp).bind(Validators)]],
    firstName: ['', Validators.required.bind(Validators)],
    lastName: ['', Validators.required.bind(Validators)],
    profession: [''],
  }, {validators: [confirmPasswordValidator]});

  constructor(
    private readonly _formBuilder: FormBuilder,
    private readonly _loggerService: LoggerService,
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _authenticationRestApiService: AuthenticationRestApiService,
    private readonly _translocoService: TranslocoService,
    private readonly _snackBar: MatSnackBar,
    private readonly _navigationService: NavigationService,
  ) {
    if (Utils.isNullOrUndefined(this._activatedRoute.snapshot.params['token'])) {
      this._loggerService.error('No token provided');
    }
    this.token = this._activatedRoute.snapshot.params['token'] as string;
  }

  public get isPractitioner(): boolean {
    return this.user?.roles[0] === UserRolesEnum.PRACTITIONER;
  }

  public ngOnInit(): void {
    this._authenticationRestApiService.requestUserDataFromRegistrationToken(this.token).subscribe({
      next: (response: ApiUserDataFromRegistrationTokenResponse) => {
        this.user = response;
        this.formGroup.controls.email.setValue(this.user.email);
        this.formGroup.controls.firstName.setValue(this.user.firstName);
        this.formGroup.controls.lastName.setValue(this.user.lastName);
        this.formGroup.controls.profession.setValue(this.user.profession);
        if (this.isPractitioner) {
          this.formGroup.controls.profession.setValidators([Validators.required.bind(Validators)]);
          this.formGroup.controls.profession.updateValueAndValidity();
        }
        this.isLoading = false;
      },
      error: (error: HttpErrorResponse) => this.handleError(error),
    });
  }

  public submitRegistration(): void {
    if (this.formGroup.invalid || Utils.isNull(this.user)) {
      return;
    }
    const registrationData: ApiRegistrationRequest = {
      id: this.user.id,
      token: this.token,
      password: this.formGroup.controls.password.value as string,
      firstName: this.formGroup.controls.firstName.value as string,
      lastName: this.formGroup.controls.lastName.value as string,
      profession: this.formGroup.controls.profession.value as string,
    };
    this._authenticationRestApiService.requestRegistration(registrationData).subscribe({
      next: () => this.handleSuccess(),
      error: (error: HttpErrorResponse) => this.handleError(error),
    });
  }

  private async handleSuccess(): Promise<void> {
    this._snackBar.open(
      this._translocoService.translate('AUTHENTICATION.REGISTER.SUCCESS'),
      '',
      {duration: 7000},
    );
    await this._navigationService.navigateTo(RoutesEnum.AUTHENTICATION);
  }

  private handleError(error: HttpErrorResponse): void {
    switch (error.status) {
      case HttpStatusCode.NotFound as number:
        this.error = this._translocoService.translate('AUTHENTICATION.REGISTER.TOKEN_ERROR');
        return;
      case HttpStatusCode.BadRequest as number:
      case HttpStatusCode.UnprocessableEntity as number:
        if (HttpUtils.isSymfonyKnownError(error, ApiRegistrationKnownErrorsEnum)) {
          this.error = this._translocoService.translate('AUTHENTICATION.REGISTER.' +
            HttpUtils.getSymfonyKnownErrorDetailMessage(error));
          return;
        }
        if (this.hasPasswordViolation(error)) {
          this.error = this._translocoService.translate('AUTHENTICATION.REGISTER.PASSWORD_ERROR');
          return;
        }
        this.error = this._translocoService.translate('AUTHENTICATION.REGISTER.PARAM_ERROR');
        return;
      default:
        this.error = this._translocoService.translate('SHARED.ERRORS.UNKNOWN');
    }
  }

  private hasPasswordViolation(error: HttpErrorResponse): boolean {
    const violations = (error.error as { violations?: Array<{ propertyPath?: string }> })?.violations;
    if (!Array.isArray(violations)) {
      return false;
    }
    return violations.some((v: { propertyPath?: string }) => v.propertyPath === 'password');
  }
}
